import { CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE, CHECKOUT_ITEM_TYPE_LE_HOTEL, CHECKOUT_ITEM_TYPE_TOUR_V1 } from 'constants/checkout'
import { RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { BundleOfferPackageOptionView, OfferPackageOptionView } from 'selectors/offerPageSelectors'
import uuidV4 from 'lib/uuidV4'
import { PackageOptionViewByRoom } from 'selectors/multipleRoomBookingSelectors'
import { addGTMEvent, fireABTestGAEvent } from 'api/googleTagManager'
import { addAccommodationToCheckout, addBundleAndSaveToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import { OFFER_TYPE_ALWAYS_ON_ANALYTICS } from 'constants/offer'
import { TourDate } from 'api/reservation'
import { addToCartAccommodationEvent } from 'analytics/optimizely/events'
import * as Analytics from 'analytics/analytics'

type Period = { checkIn: string, checkOut: string, duration?: number }

export function generateMultipleRoomCheckoutItems(
  selectedPackagesByRoom: PackageOptionViewByRoom,
  offer: App.Offer,
  dates: Period,
  roomOccupants: App.RoomOccupants[],
  currencyCode: string,
): App.Checkout.InstantBookingLEHotelItem[] {
  return Object.entries(selectedPackagesByRoom).map(([roomId, packageOption]) => {
    const occupancy = roomOccupants.find(room => room.roomId === roomId)
    const reservationType = RESERVATION_TYPE_INSTANT_BOOKING

    addGTMEvent(addAccommodationToCheckout(offer, packageOption, reservationType, currencyCode))

    return ({
      itemId: uuidV4(),
      offerId: offer.id,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
      reservationType,
      packageId: packageOption.package.id,
      roomRateId: packageOption.package.roomRate.id,
      duration: packageOption.package.duration,
      occupancy,
      ...dates,
    })
  })
}

export function generateBundleAndSaveCheckoutItems(
  offer: App.Offer,
  packageOption: BundleOfferPackageOptionView,
  dates: Record<string, Required<Period>>,
  roomOccupants: App.Occupants[],
  currencyCode: string,
): Array<App.Checkout.BundleAndSaveItem> {
  return roomOccupants.map((occupancy): App.Checkout.BundleAndSaveItem => {
    addGTMEvent(addBundleAndSaveToCheckout(offer, packageOption, currencyCode))

    return {
      itemId: uuidV4(),
      offerId: offer.id,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE,
      packageId: packageOption.bundlePackageId,
      occupancy,
      dates,
    }
  })
}

export function generateVillaCheckoutItem(
  offer: App.Offer | App.OfferSummary,
  packageOption: OfferPackageOptionView,
  dates: Period,
  occupancy: App.Occupants,
  currencyCode: string,
): App.Checkout.VillaItem {
  const reservationType = RESERVATION_TYPE_INSTANT_BOOKING

  addGTMEvent(addAccommodationToCheckout(offer, packageOption, reservationType, currencyCode))
  Analytics.track('add-to-cart-accommodation', addToCartAccommodationEvent())

  if (offer.analyticsType === OFFER_TYPE_ALWAYS_ON_ANALYTICS) {
    fireABTestGAEvent({
      category: 'lpc-add-to-cart',
      action: 'click',
    }, false)
  }

  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: 'villa',
    reservationType,
    packageId: packageOption.package.id,
    roomRateId: packageOption.package.roomRate!.id,
    duration: packageOption.package.duration,
    occupancy,
    ...dates,
  }
}

export function generateAccommodationCheckoutItems(
  offer: App.Offer | App.OfferSummary,
  packageOption: OfferPackageOptionView,
  dates: Period,
  roomOccupants: App.Occupants[],
  currencyCode: string,
): App.Checkout.InstantBookingLEHotelItem[] {
  return roomOccupants.map(occupancy => {
    const reservationType = RESERVATION_TYPE_INSTANT_BOOKING

    addGTMEvent(addAccommodationToCheckout(offer, packageOption, reservationType, currencyCode))
    Analytics.track('add-to-cart-accommodation', addToCartAccommodationEvent())

    if (offer.analyticsType === OFFER_TYPE_ALWAYS_ON_ANALYTICS) {
      fireABTestGAEvent({
        category: 'lpc-add-to-cart',
        action: 'click',
      }, false)
    }

    return {
      itemId: uuidV4(),
      offerId: offer.id,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
      reservationType,
      packageId: packageOption.package.id,
      roomRateId: packageOption.package.roomRate.id,
      duration: packageOption.package.duration,
      occupancy,
      ...dates,
    }
  })
}

export function generateBedbankAccommodationCheckoutItems(
  offer: App.BedbankOffer,
  sessionId: string,
  pkg: App.BedbankPackage,
  rate: App.BedbankRate,
  dates: Period,
  roomOccupants: App.Occupants[],
): App.Checkout.BedbankHotelItem[] {
  return roomOccupants.map(occupancy => ({
    offerId: offer.id,
    sessionId,
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_BEDBANK,
    roomId: pkg.roomId,
    roomRateId: rate.id,
    bedGroupId: rate.bedGroups[0].id,
    occupancy,
    duration: dates.duration,
    ...dates,
  }))
}

export function generateBNBLCheckoutItem(
  offer: App.Offer,
  packageOption: App.Package,
  numOfRooms = 1,
): App.Checkout.BNBLLEHotelItem[] {
  return new Array(numOfRooms).fill(null).map(() => ({
    offerId: offer.id,
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_BOOK_LATER,
    packageId: packageOption.id,
    roomRateId: packageOption.roomRate!.id,
    duration: packageOption.duration,
  }))
}

export function generateSelectDateCheckoutItem(
  offer: App.Offer,
  packageOption: App.Package,
  occupancy: App.Occupants,
  orderItemId: string,
): App.Checkout.BNBLLEHotelItem {
  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_BOOK_LATER,
    packageId: packageOption.id,
    roomRateId: packageOption.roomRate!.id,
    duration: packageOption.duration,
    occupancy,
    orderItemId,
  }
}

export function generateTourCheckoutItems(
  offer: App.Offer,
  pkg: App.Package,
  roomOccupants: App.Occupants[],
  date: Pick<TourDate, 'startDate' | 'endDate'>,
): App.Checkout.LETourV1Item[] {
  return roomOccupants.map(occupancy => {
    return {
      itemId: uuidV4(),
      offerId: offer.id,
      transactionKey: uuidV4(),
      itemType: CHECKOUT_ITEM_TYPE_TOUR_V1,
      reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
      packageId: pkg.id,
      duration: pkg.duration,
      occupancy,
      startDate: date.startDate,
      endDate: date.endDate,
    }
  })
}

export function generatePackageUpgradeItem(
  offer: App.Offer | App.OfferSummary,
  orderItem: App.OrderItem,
  selectedOption?: OfferPackageOptionView,
): App.Checkout.InstantBookingLEHotelItem {
  const pkgOption = orderItem.package as App.OrderItemHotelPackage
  const reservation = orderItem.reservation

  if (!reservation) {
    throw new Error('Reservation is required for package upgrade')
  }

  const occupancy: App.Occupants = {
    adults: reservation.adults,
    children: reservation.children,
    infants: reservation.infants,
    childrenAge: reservation.childrenAge,
  }

  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: selectedOption?.package.id ?? pkgOption.id,
    roomRateId: selectedOption?.package.roomRate?.id ?? pkgOption.roomRateId,
    duration: selectedOption?.package.duration ?? pkgOption.duration,
    checkIn: reservation.startDate,
    checkOut: reservation.endDate,
    occupancy,
    orderItemId: orderItem.id,
    isUpdated: !!selectedOption,
  }
}

export function generateChangeDatesItem(
  offer: App.Offer | App.OfferSummary,
  orderItem: App.OrderItem,
  selectedDate?: App.CalendarDay,
): App.Checkout.InstantBookingLEHotelItem {
  const pkgOption = orderItem.package as App.OrderItemHotelPackage
  const reservation = orderItem.reservation

  if (!reservation) {
    throw new Error('Reservation is required for package upgrade')
  }

  const occupancy: App.Occupants = {
    adults: reservation.adults,
    children: reservation.children,
    infants: reservation.infants,
    childrenAge: reservation.childrenAge,
  }

  return {
    itemId: uuidV4(),
    offerId: offer.id,
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: pkgOption.id,
    roomRateId: pkgOption.roomRateId,
    duration: pkgOption.duration,
    checkIn: selectedDate?.checkIn ?? reservation.startDate,
    checkOut: selectedDate?.checkOut ?? reservation.endDate,
    occupancy,
    orderItemId: orderItem.id,
    isUpdated: !!selectedDate,
  }
}
